import { LogoMark } from "@/components/Logo";
import { Phone, Mail, MapPin } from "lucide-react";
import { WA_PHONE, WA_DISPLAY, EMAIL, buildWaLink } from "@/lib/whatsapp";

/* Business card layout — 85mm × 55mm (standard).
   Front + Back shown side-by-side at scale for review/print.
*/
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
  buildWaLink("Hi Talibnaz, I saw your card and wanted to say hi. ")
)}&size=400x400&color=0B1320&bgcolor=FAF9F6&margin=0&format=svg`;

const CARD_W = 510; // 85mm at ~6px/mm
const CARD_H = 330; // 55mm

const CardFront = () => (
  <div
    className="relative bg-[#0B1320] text-white rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(11,19,32,0.45)]"
    style={{ width: CARD_W, height: CARD_H }}
  >
    <div
      aria-hidden
      className="absolute -bottom-24 -end-24 h-64 w-64 rounded-full bg-[#FF5A00]/25 blur-3xl"
    />
    {/* URL — bottom right corner */}
    <div className="absolute bottom-6 right-8 text-[11px] font-bold uppercase tracking-[0.24em] text-[#FF5A00]">
      talibnaz.com
    </div>
    <div className="relative h-full p-8 flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <LogoMark size={64} />
        <div className="text-end space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#FF5A00]">
            Electric · Plumbing
          </div>
          <div
            className="text-[11px] font-bold text-white/70"
            style={{ fontFamily: "Tajawal, sans-serif" }}
            dir="rtl"
          >
            كهرباء · سباكة
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-end gap-3">
          <h2
            className="text-5xl font-black tracking-tight leading-none"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Talibnaz
          </h2>
          <span
            className="text-2xl font-bold leading-none pb-1 text-white/70"
            style={{ fontFamily: "Tajawal, sans-serif" }}
            dir="rtl"
          >
            طالبناز
          </span>
        </div>
        <p className="mt-3 text-sm text-white/70 max-w-[32ch] leading-snug">
          A hand around the house — Al Muruj &amp; nearby.
        </p>
        <p
          className="mt-1 text-sm text-white/70 max-w-[32ch] leading-snug"
          style={{ fontFamily: "Tajawal, sans-serif" }}
          dir="rtl"
        >
          يد صديقة في البيت — المروج وما حولها.
        </p>
      </div>
    </div>
  </div>
);

const CardBack = () => (
  <div
    className="relative bg-[#FAF9F6] text-[#0B1320] rounded-2xl overflow-hidden border border-[#E2DFD2] shadow-[0_20px_60px_-20px_rgba(11,19,32,0.35)]"
    style={{ width: CARD_W, height: CARD_H }}
  >
    <div className="h-full p-8 flex items-stretch justify-between gap-6">
      {/* Left: contact block */}
      <div className="flex-1 flex flex-col justify-between">
        <div className="flex items-center gap-2.5">
          <LogoMark size={30} />
          <div className="flex items-baseline gap-2">
            <span
              className="text-lg font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Talibnaz
            </span>
            <span
              className="text-sm font-bold text-[#475569]"
              style={{ fontFamily: "Tajawal, sans-serif" }}
              dir="rtl"
            >
              طالبناز
            </span>
          </div>
        </div>

        <ul className="space-y-2.5">
          <li className="flex items-center gap-3">
            <span className="h-8 w-8 rounded-lg bg-[#0B1320] text-[#FF5A00] grid place-items-center">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            <span dir="ltr" className="text-sm font-bold tracking-tight">
              {WA_DISPLAY}
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="h-8 w-8 rounded-lg bg-[#0B1320] text-[#FF5A00] grid place-items-center">
              <Mail size={14} strokeWidth={2.5} />
            </span>
            <span className="text-sm font-semibold">{EMAIL}</span>
          </li>
          <li className="flex items-center gap-3">
            <span className="h-8 w-8 rounded-lg bg-[#0B1320] text-[#FF5A00] grid place-items-center">
              <MapPin size={14} strokeWidth={2.5} />
            </span>
            <div className="text-sm font-semibold leading-tight">
              <div>Al Muruj, Riyadh</div>
              <div className="text-[#475569] text-xs" style={{ fontFamily: "Tajawal, sans-serif" }} dir="rtl">
                المروج، الرياض
              </div>
            </div>
          </li>
        </ul>

        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#475569]">
          talibnaz.com
        </div>
      </div>

      {/* Right: QR block */}
      <div className="flex flex-col items-center justify-between py-2">
        <div className="bg-white p-2 rounded-xl border border-[#E2DFD2]">
          <img
            src={QR_URL}
            alt="Scan to WhatsApp"
            width={120}
            height={120}
            className="block"
          />
        </div>
        <div className="text-center mt-2 leading-tight">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#0B1320]">
            Scan → WhatsApp
          </div>
          <div
            className="text-[10px] font-bold text-[#0B1320] mt-0.5"
            style={{ fontFamily: "Tajawal, sans-serif" }}
            dir="rtl"
          >
            امسح → واتساب
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CardPage = () => {
  return (
    <div className="min-h-screen bg-[#F3F1E7] py-16 print:py-0 print:bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-10 print:hidden">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00]">
          Business Card · Preview
        </div>
        <h1
          className="mt-3 text-4xl md:text-5xl font-black tracking-tight text-[#0B1320] leading-[1.05]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Talibnaz card.
        </h1>
        <p className="mt-4 text-[#475569] max-w-2xl">
          Standard 85×55mm. Front (dark) + Back (light with QR). QR code opens WhatsApp directly with a hi message pre-filled.
        </p>
      </div>

      <div className="mt-12 grid place-items-center gap-8 print:mt-0">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569] print:hidden">
          Front
        </div>
        <CardFront />

        <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-[#475569] print:hidden">
          Back
        </div>
        <CardBack />
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 mt-14 print:hidden">
        <div className="bg-white rounded-3xl p-8 border border-[#E2DFD2]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00]">
            How to print
          </div>
          <ol className="mt-4 space-y-3 text-sm text-[#0B1320] leading-relaxed list-decimal ps-5">
            <li>Browser mein <code className="px-1.5 py-0.5 rounded bg-[#F3F1E7] text-[#0B1320]">Ctrl+P</code> (Windows) ya <code className="px-1.5 py-0.5 rounded bg-[#F3F1E7]">⌘+P</code> (Mac) dabao.</li>
            <li>"Save as PDF" select karo — 2 pages ka PDF ban jayega (front + back).</li>
            <li>Ya screenshot le lo dono cards ka.</li>
            <li>PDF ya PNG kisi bhi local Riyadh print shop pe le jao — bolo "85×55mm business card, 200 cards". SAR 30–80 mein ready.</li>
          </ol>

          <div className="mt-6 pt-6 border-t border-[#E2DFD2]">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-3">
              QR test
            </div>
            <p className="text-sm text-[#475569]">
              Apna phone camera QR pe rakho — WhatsApp chat direct khulega with pre-filled hi message. Print se pehle ek baar test kar lena.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          @page { margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default CardPage;
